import type {
    GA4SearchTermType,
    GTMEcommerceReturnType,
    GTMItemData,
    GTMItemsReturnType
} from '../GTM.types';
import GTMBaseModel from '../GTMBaseModel';
import { useFallbackForEmptyCategories } from '../GTMHelpers';

type CalendarType = 'fixed' | 'flexible';

interface GTMViewListItemEcommerceReturnType {
    item_list_id: string;
    item_list_name: string;
    items: GTMItemsReturnType[];
}

interface GTMViewItemListReturnType
    extends GTMEcommerceReturnType<GTMViewListItemEcommerceReturnType> {
    item_count: number;
    search_term?: string;
    search_type?: GA4SearchTermType;
    search_destination?: string;
    calendar_type?: CalendarType;
    flexible_duration?: string;
    flexible_months?: string;
    fixed_date_flexible_arrival?: string;
    custom_adults?: number;
    custom_children?: number;
    custom_babies?: number;
    custom_pets?: number;
    fixed_date_arrival?: string;
    fixed_date_departure?: string;
}

export interface GTMViewItemListModelData {
    listId: string;
    listName: string;
    items: GTMItemData[];
    itemCount: number;
    searchTerm?: string;
    searchType?: GA4SearchTermType;
    searchDestination?: string;
    calendarType?: CalendarType;
    flexibleDuration?: string;
    flexibleMonths?: string;
    flexibleArrivalDate?: string;
    fixedDateArrival?: string;
    fixedDateDeparture?: string;
    adults?: string;
    children?: string;
    babies?: string;
    pets?: string;
}

export default class GTMViewItemListModel extends GTMBaseModel {
    public static parseToGTMData({
        listId,
        listName,
        items,
        itemCount,
        searchTerm,
        searchType,
        searchDestination,
        calendarType,
        flexibleDuration,
        flexibleMonths,
        flexibleArrivalDate,
        fixedDateArrival,
        fixedDateDeparture,
        adults,
        children,
        babies,
        pets
    }: GTMViewItemListModelData): GTMViewItemListReturnType {
        return {
            ...GTMBaseModel.getBaseData(),
            ga4_property: 'web_event',
            ga4_event_name: 'view_item_list',
            item_count: itemCount,
            search_term: searchTerm,
            search_type: searchType,
            search_destination: searchDestination,
            calendar_type: calendarType,
            flexible_duration: flexibleDuration,
            flexible_months: flexibleMonths,
            fixed_date_flexible_arrival: flexibleArrivalDate,
            custom_adults: typeof adults === 'string' ? Number(adults) : undefined,
            custom_children: typeof children === 'string' ? Number(children) : undefined,
            custom_babies: typeof babies === 'string' ? Number(babies) : undefined,
            custom_pets: typeof pets === 'string' ? Number(pets) : undefined,
            fixed_date_arrival: fixedDateArrival,
            fixed_date_departure: fixedDateDeparture,
            ecommerce: {
                item_list_id: listId,
                item_list_name: listName,
                items: items.map((item, index) => ({
                    index: index,
                    item_id: item.id,
                    item_name: item.title,
                    ...useFallbackForEmptyCategories(item),
                    discount: item.discount,
                    quantity: item.quantity ?? 1,
                    price: item.pricePerNight,
                    instant_booking: item.instantBooking
                }))
            }
        };
    }
}
